import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import {
  Users, Search, ChevronDown, ChevronUp, Mail, Phone, Building2,
  MessageSquare, ShoppingBag, Truck, Clock, ArrowRight, X,
  User, Calendar, Activity, HelpCircle, AlertCircle, FileText,
  CheckCircle2, Package, Flame, Star, CalendarClock, StickyNote, RefreshCw
} from 'lucide-react';
import ShareExportMenu from './ShareExportMenu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUSES = {
  new: { label: 'New', color: '#ef4444', bg: '#fef2f2' },
  contacted: { label: 'Contacted', color: '#f97316', bg: '#fff7ed' },
  quoted: { label: 'Quoted', color: '#eab308', bg: '#fefce8' },
  confirmed: { label: 'Confirmed', color: '#22c55e', bg: '#f0fdf4' },
  shipped: { label: 'Shipped', color: '#3b82f6', bg: '#eff6ff' },
};

const STATUS_ICONS = {
  new: AlertCircle, contacted: Mail, quoted: FileText,
  confirmed: CheckCircle2, shipped: Truck,
};

const TYPE_CONFIG = {
  order: { label: 'Order', icon: ShoppingBag, color: '#E8520A', bg: '#FFF0E8' },
  kei_truck_order: { label: 'Kei Truck', icon: Truck, color: '#3b82f6', bg: '#eff6ff' },
  contact: { label: 'Contact', icon: MessageSquare, color: '#8b5cf6', bg: '#f5f3ff' },
};

const SOURCE_BADGES = {
  chat: { icon: MessageSquare, color: '#8b5cf6', bg: '#f5f3ff', label: 'Chat' },
  order: { icon: ShoppingBag, color: '#E8520A', bg: '#FFF0E8', label: 'Order' },
  contact: { icon: Mail, color: '#22c55e', bg: '#f0fdf4', label: 'Contact' },
};

const fmt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const fmtTime = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

const CustomersTab = ({ authHeaders, flash }) => {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('last_seen');
  const [sortOrder, setSortOrder] = useState('desc');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [expandedEmail, setExpandedEmail] = useState(null);
  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [expandedChat, setExpandedChat] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [activeView, setActiveView] = useState('activity'); // 'activity' | 'timeline'
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);

  const loadCustomers = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/portal/admin/customers/unified`, { headers: authHeaders() });
      setCustomers(res.data);
    } catch {
      flash('Failed to load customers', true);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, flash]);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);

  const loadActivity = async (email) => {
    setActivityLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/portal/admin/customers/${encodeURIComponent(email)}/activity`, { headers: authHeaders() });
      setActivity(res.data);
    } catch {
      flash('Failed to load customer activity', true);
    } finally {
      setActivityLoading(false);
    }
  };

  const toggleExpand = (email) => {
    if (expandedEmail === email) {
      setExpandedEmail(null);
      setActivity(null);
      setTimeline([]);
      setExpandedChat(null);
      setExpandedOrder(null);
      setActiveView('activity');
    } else {
      setExpandedEmail(email);
      setExpandedChat(null);
      setExpandedOrder(null);
      setActiveView('activity');
      loadActivity(email);
      loadTimeline(email);
    }
  };

  const loadTimeline = async (email) => {
    setTimelineLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/portal/admin/customers/${encodeURIComponent(email)}/timeline`, { headers: authHeaders() });
      setTimeline(res.data);
    } catch {
      // timeline is optional, don't flash error
    } finally {
      setTimelineLoading(false);
    }
  };

  const filtered = customers
    .filter(c => {
      if (sourceFilter !== 'all' && !c.sources.includes(sourceFilter)) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      let cmp = 0;
      if (sortField === 'last_seen') {
        cmp = (a.last_seen || '').localeCompare(b.last_seen || '');
      } else if (sortField === 'name') {
        cmp = (a.name || '').localeCompare(b.name || '');
      } else if (sortField === 'activity') {
        cmp = a.total_activity - b.total_activity;
      }
      return sortOrder === 'desc' ? -cmp : cmp;
    });

  const sourceCounts = {
    chat: customers.filter(c => c.sources.includes('chat')).length,
    order: customers.filter(c => c.sources.includes('order')).length,
    contact: customers.filter(c => c.sources.includes('contact')).length,
  };

  const exportCustomersCSV = () => {
    const headers = ['Name', 'Email', 'Sources', 'Chat Sessions', 'Orders', 'Contacts', 'Total Activity', 'First Seen', 'Last Seen'];
    const rows = filtered.map(c => [
      c.name || '', c.email || '', (c.sources || []).join('; '),
      c.chat_sessions || 0, c.orders || 0, c.contacts || 0,
      c.total_activity || 0,
      c.first_seen ? new Date(c.first_seen).toLocaleString() : '',
      c.last_seen ? new Date(c.last_seen).toLocaleString() : '',
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customers_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportCustomersPDF = () => {
    const html = `<!DOCTYPE html><html><head><title>Customers Export</title>
<style>
  body{font-family:Arial,sans-serif;margin:32px;color:#1a1a1a}
  h1{font-size:20px;margin-bottom:4px}
  .sub{font-size:12px;color:#888;margin-bottom:24px}
  table{width:100%;border-collapse:collapse;font-size:12px}
  th{background:#f9fafb;text-align:left;padding:8px 10px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;color:#6b7280;border-bottom:2px solid #e5e7eb}
  td{padding:8px 10px;border-bottom:1px solid #f3f4f6}
  tr:hover td{background:#f9fafb}
  .badge{display:inline-block;font-size:9px;font-weight:700;padding:2px 6px;border-radius:4px;margin-right:3px}
  @media print{body{margin:16px}}
</style></head><body>
<h1>Customer Directory</h1>
<p class="sub">Exported ${new Date().toLocaleString()} · ${filtered.length} customers</p>
<table>
<thead><tr><th>Name</th><th>Email</th><th>Sources</th><th>Chats</th><th>Orders</th><th>Contacts</th><th>Last Active</th></tr></thead>
<tbody>
${filtered.map(c => `<tr>
  <td><strong>${c.name || '—'}</strong></td>
  <td>${c.email}</td>
  <td>${(c.sources || []).map(s => `<span class="badge" style="background:#f3f4f6">${s}</span>`).join('')}</td>
  <td>${c.chat_sessions || 0}</td>
  <td>${c.orders || 0}</td>
  <td>${c.contacts || 0}</td>
  <td>${fmt(c.last_seen)}</td>
</tr>`).join('')}
</tbody></table>
</body></html>`;
    const sanitized = DOMPurify.sanitize(html, { WHOLE_DOCUMENT: true, ADD_TAGS: ['style', 'html', 'head', 'body', 'meta'], ADD_ATTR: ['charset'] });
    const blob = new Blob([sanitized], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank');
    if (w) w.onload = () => { w.print(); URL.revokeObjectURL(url); };
  };

  const TIMELINE_ICONS = {
    chat: { icon: MessageSquare, color: '#8b5cf6', bg: '#f5f3ff' },
    order: { icon: ShoppingBag, color: '#E8520A', bg: '#FFF0E8' },
    kei_truck_order: { icon: Truck, color: '#3b82f6', bg: '#eff6ff' },
    contact: { icon: Mail, color: '#22c55e', bg: '#f0fdf4' },
    status_change: { icon: RefreshCw, color: '#f97316', bg: '#fff7ed' },
    note: { icon: StickyNote, color: '#eab308', bg: '#fefce8' },
  };

  return (
    <div className="p-4 sm:p-8" data-testid="customers-tab">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-black text-gray-900" data-testid="customers-title">Customers</h2>
          <p className="text-sm text-gray-500 mt-1">{customers.length} unique customers across all channels</p>
        </div>
        <ShareExportMenu
          onExportCSV={exportCustomersCSV}
          onExportPDF={exportCustomersPDF}
          shareTitle="Total Composites — Customer Directory"
        />
      </div>

      {/* Source Filter Pills */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="source-filters">
        <button
          onClick={() => setSourceFilter('all')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
            sourceFilter === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Users size={12} /> All ({customers.length})
        </button>
        {Object.entries(SOURCE_BADGES).map(([key, cfg]) => {
          const Icon = cfg.icon;
          const active = sourceFilter === key;
          return (
            <button
              key={key}
              onClick={() => setSourceFilter(active ? 'all' : key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                active ? 'shadow-sm' : 'hover:opacity-80'
              }`}
              style={active ? { backgroundColor: cfg.color, color: '#fff' } : { backgroundColor: cfg.bg, color: cfg.color }}
              data-testid={`source-filter-${key}`}
            >
              <Icon size={12} /> {cfg.label} ({sourceCounts[key] || 0})
            </button>
          );
        })}
      </div>

      {/* Search + Sort */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:border-orange-300"
            placeholder="Search by name or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid="customer-search"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={sortField}
            onChange={e => setSortField(e.target.value)}
            className="appearance-none px-3 py-2.5 border border-gray-200 rounded-xl text-xs font-bold text-gray-600 focus:outline-none bg-white"
            data-testid="customer-sort"
          >
            <option value="last_seen">Last Active</option>
            <option value="name">Name</option>
            <option value="activity">Activity</option>
          </select>
          <button
            onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
            className="flex items-center gap-1 px-3 py-2.5 border border-gray-200 rounded-xl text-xs font-bold text-gray-600 hover:border-gray-300"
            data-testid="customer-sort-dir"
          >
            {sortOrder === 'desc' ? <ChevronDown size={13} /> : <ChevronUp size={13} />}
          </button>
        </div>
      </div>

      {/* Customer List */}
      {loading ? (
        <div className="text-center py-12 text-gray-400 text-sm">Loading customers...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <Users size={40} className="mx-auto mb-3 text-gray-200" />
          <p className="text-sm font-bold text-gray-400">No customers found</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="customers-list">
          {filtered.map(cust => {
            const isExpanded = expandedEmail === cust.email;
            return (
              <div key={cust.email} className="bg-white rounded-xl border border-gray-200 overflow-hidden transition-all hover:border-gray-300">
                <button
                  onClick={() => toggleExpand(cust.email)}
                  className="w-full flex items-center gap-3 sm:gap-4 px-4 py-3 text-left"
                  data-testid={`customer-row-${cust.email}`}
                >
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-800 to-gray-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-white">
                      {(cust.name || cust.email || '?').charAt(0).toUpperCase()}
                    </span>
                  </div>

                  {/* Name + Email */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold text-gray-900 truncate">{cust.name || 'Unknown'}</span>
                      {/* Source badges */}
                      {cust.sources.map(s => {
                        const cfg = SOURCE_BADGES[s];
                        if (!cfg) return null;
                        const Icon = cfg.icon;
                        return (
                          <span key={s} className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                            style={{ backgroundColor: cfg.bg, color: cfg.color }}>
                            <Icon size={8} /> {cfg.label}
                          </span>
                        );
                      })}
                    </div>
                    <span className="text-xs text-gray-400 truncate block">{cust.email}</span>
                  </div>

                  {/* Activity Counts */}
                  <div className="hidden sm:flex items-center gap-3 flex-shrink-0">
                    {cust.chat_sessions > 0 && (
                      <div className="flex items-center gap-1 text-xs text-purple-600" title="Chat sessions">
                        <MessageSquare size={11} /> {cust.chat_sessions}
                      </div>
                    )}
                    {cust.orders > 0 && (
                      <div className="flex items-center gap-1 text-xs" style={{ color: '#E8520A' }} title="Orders">
                        <ShoppingBag size={11} /> {cust.orders}
                      </div>
                    )}
                    {cust.contacts > 0 && (
                      <div className="flex items-center gap-1 text-xs text-green-600" title="Contact forms">
                        <Mail size={11} /> {cust.contacts}
                      </div>
                    )}
                  </div>

                  {/* Last Seen */}
                  <div className="hidden md:block flex-shrink-0 text-right">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Last Active</p>
                    <p className="text-xs text-gray-600">{fmt(cust.last_seen)}</p>
                  </div>

                  <ArrowRight size={14} className={`text-gray-300 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`} />
                </button>

                {/* Expanded Activity */}
                {isExpanded && (
                  <div className="border-t border-gray-100 bg-gray-50 px-4 py-4" data-testid="customer-activity">
                    {/* Summary Bar */}
                    <div className="flex flex-wrap items-center gap-4 mb-4 pb-3 border-b border-gray-200">
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <User size={12} /> <strong className="text-gray-800">{cust.name || 'Unknown'}</strong>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <Mail size={12} /> {cust.email}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <Calendar size={12} /> First seen: {fmt(cust.first_seen)}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <Activity size={12} /> {cust.total_activity} total interactions
                      </div>
                    </div>

                    {/* View Toggle: Activity / Timeline */}
                    <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-0.5 w-fit" data-testid="view-toggle">
                      <button
                        onClick={() => setActiveView('activity')}
                        className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                          activeView === 'activity' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                        }`}
                        data-testid="view-activity-btn"
                      >
                        Activity
                      </button>
                      <button
                        onClick={() => setActiveView('timeline')}
                        className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                          activeView === 'timeline' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                        }`}
                        data-testid="view-timeline-btn"
                      >
                        Timeline
                      </button>
                    </div>

                    {/* Activity View */}
                    {activeView === 'activity' && (
                      <>
                    {activityLoading ? (
                      <div className="text-center py-6 text-gray-400 text-sm">Loading activity...</div>
                    ) : activity ? (
                      <div className="space-y-4">
                        {/* Chat Sessions */}
                        {activity.chats.length > 0 && (
                          <div data-testid="customer-chats">
                            <div className="flex items-center gap-2 mb-2">
                              <MessageSquare size={14} className="text-purple-500" />
                              <h4 className="text-xs font-bold uppercase tracking-widest text-gray-500">
                                Chat Sessions ({activity.chats.length})
                              </h4>
                            </div>
                            <div className="space-y-1.5">
                              {activity.chats.map(chat => (
                                <div key={chat.session_id} className="bg-white rounded-lg border border-gray-100 overflow-hidden">
                                  <button
                                    onClick={() => setExpandedChat(expandedChat === chat.session_id ? null : chat.session_id)}
                                    className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-purple-50/30 transition-colors"
                                    data-testid={`chat-session-${chat.session_id}`}
                                  >
                                    <div className="w-6 h-6 rounded bg-purple-50 flex items-center justify-center flex-shrink-0">
                                      <MessageSquare size={11} className="text-purple-500" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-semibold text-gray-700 truncate">{chat.preview || 'Chat session'}</p>
                                      <p className="text-[10px] text-gray-400">{chat.message_count} messages</p>
                                    </div>
                                    <span className="text-[10px] text-gray-400 flex-shrink-0">{fmt(chat.last_message)}</span>
                                    <ArrowRight size={10} className={`text-gray-300 transition-transform ${expandedChat === chat.session_id ? 'rotate-90' : ''}`} />
                                  </button>
                                  {expandedChat === chat.session_id && (
                                    <div className="px-3 pb-3 border-t border-gray-50 bg-gray-50">
                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2 text-xs">
                                        <div><span className="text-gray-400">Session ID:</span> <span className="font-mono text-gray-600 break-all">{chat.session_id}</span></div>
                                        <div><span className="text-gray-400">Messages:</span> <span className="font-bold text-gray-700">{chat.message_count}</span></div>
                                        <div><span className="text-gray-400">Started:</span> <span className="text-gray-600">{fmt(chat.first_message)} {fmtTime(chat.first_message)}</span></div>
                                        <div><span className="text-gray-400">Last message:</span> <span className="text-gray-600">{fmt(chat.last_message)} {fmtTime(chat.last_message)}</span></div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Orders & Contacts */}
                        {activity.orders.length > 0 && (
                          <div data-testid="customer-orders">
                            <div className="flex items-center gap-2 mb-2">
                              <Package size={14} style={{ color: '#E8520A' }} />
                              <h4 className="text-xs font-bold uppercase tracking-widest text-gray-500">
                                Orders & Contacts ({activity.orders.length})
                              </h4>
                            </div>
                            <div className="space-y-1.5">
                              {activity.orders.map(order => {
                                const tc = TYPE_CONFIG[order.type] || { label: order.type, icon: HelpCircle, color: '#6b7280', bg: '#f3f4f6' };
                                const TypeIcon = tc.icon;
                                const st = STATUSES[order.status] || STATUSES.new;
                                const StIcon = STATUS_ICONS[order.status] || AlertCircle;
                                const isOrdExpanded = expandedOrder === order.id;
                                return (
                                  <div key={order.id} className="bg-white rounded-lg border border-gray-100 overflow-hidden">
                                    <button
                                      onClick={() => setExpandedOrder(isOrdExpanded ? null : order.id)}
                                      className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-orange-50/30 transition-colors"
                                      data-testid={`order-item-${order.id}`}
                                    >
                                      <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: tc.bg }}>
                                        <TypeIcon size={11} style={{ color: tc.color }} />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs font-semibold text-gray-700">{order.product || tc.label}</span>
                                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: st.bg, color: st.color }}>
                                            {st.label}
                                          </span>
                                          {order.priority && (() => {
                                            const PRIO = { hot: { color: '#dc2626', bg: '#fef2f2' }, warm: { color: '#f97316', bg: '#fff7ed' }, cold: { color: '#6b7280', bg: '#f3f4f6' }, vip: { color: '#7c3aed', bg: '#f5f3ff' } };
                                            const pr = PRIO[order.priority];
                                            return pr ? (
                                              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: pr.bg, color: pr.color }}>
                                                {order.priority.toUpperCase()}
                                              </span>
                                            ) : null;
                                          })()}
                                        </div>
                                        <p className="text-[10px] text-gray-400 truncate">{order.message?.slice(0, 60) || 'No message'}</p>
                                      </div>
                                      <span className="text-[10px] text-gray-400 flex-shrink-0">{fmt(order.created_at)}</span>
                                      <ArrowRight size={10} className={`text-gray-300 transition-transform ${isOrdExpanded ? 'rotate-90' : ''}`} />
                                    </button>
                                    {isOrdExpanded && (
                                      <div className="px-3 pb-3 border-t border-gray-50 bg-gray-50">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2 text-xs">
                                          <div><span className="text-gray-400">Type:</span> <span className="font-bold" style={{ color: tc.color }}>{tc.label}</span></div>
                                          <div className="flex items-center gap-1">
                                            <span className="text-gray-400">Status:</span>
                                            <StIcon size={10} style={{ color: st.color }} />
                                            <span className="font-bold" style={{ color: st.color }}>{st.label}</span>
                                          </div>
                                          {order.product && <div><span className="text-gray-400">Product:</span> <span className="text-gray-700 font-medium">{order.product}</span></div>}
                                          {order.customer_phone && <div><span className="text-gray-400">Phone:</span> <span className="text-gray-700">{order.customer_phone}</span></div>}
                                          {order.customer_company && <div><span className="text-gray-400">Company:</span> <span className="text-gray-700">{order.customer_company}</span></div>}
                                          <div><span className="text-gray-400">Source:</span> <span className="text-gray-700">{order.source_page || '—'}</span></div>
                                          <div><span className="text-gray-400">Created:</span> <span className="text-gray-600">{fmt(order.created_at)} {fmtTime(order.created_at)}</span></div>
                                          {order.series && <div><span className="text-gray-400">Series:</span> <span className="text-gray-700">{order.series}</span></div>}
                                          {order.follow_up_date && <div><span className="text-gray-400">Follow-up:</span> <span className="text-blue-600 font-bold">{fmt(order.follow_up_date)}</span></div>}
                                        </div>
                                        {order.details && Object.keys(order.details).length > 0 && (
                                          <div className="mt-2 pt-2 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                                            {Object.entries(order.details).map(([k, v]) => (
                                              <div key={k}><span className="text-gray-400">{k.replace(/_/g, ' ')}:</span> <span className="text-gray-700">{String(v)}</span></div>
                                            ))}
                                          </div>
                                        )}
                                        {order.message && (
                                          <div className="mt-2 pt-2 border-t border-gray-100">
                                            <p className="text-xs text-gray-400 mb-1">Message:</p>
                                            <p className="text-xs text-gray-700 whitespace-pre-wrap">{order.message}</p>
                                          </div>
                                        )}
                                        {order.notes && (
                                          <div className="mt-2 pt-2 border-t border-gray-100 bg-yellow-50 -mx-3 px-3 pb-1 rounded-b-lg">
                                            <p className="text-xs text-yellow-700"><strong>Notes:</strong> {order.notes}</p>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {activity.chats.length === 0 && activity.orders.length === 0 && (
                          <div className="text-center py-6 text-gray-400 text-sm">No activity found</div>
                        )}
                      </div>
                    ) : null}
                      </>
                    )}

                    {/* Timeline View */}
                    {activeView === 'timeline' && (
                      <div data-testid="customer-timeline">
                        {timelineLoading ? (
                          <div className="text-center py-6 text-gray-400 text-sm">Loading timeline...</div>
                        ) : timeline.length === 0 ? (
                          <div className="text-center py-6 text-gray-400 text-sm">No timeline events</div>
                        ) : (
                          <div className="relative pl-6">
                            {/* Vertical line */}
                            <div className="absolute left-[11px] top-2 bottom-2 w-px bg-gray-200" />
                            <div className="space-y-3">
                              {timeline.map((event, i) => {
                                const cfg = TIMELINE_ICONS[event.type] || { icon: Activity, color: '#6b7280', bg: '#f3f4f6' };
                                const Icon = cfg.icon;
                                return (
                                  <div key={`${event.type}-${event.date}-${i}`} className="relative flex items-start gap-3">
                                    {/* Dot on timeline */}
                                    <div className="absolute -left-6 top-1 w-[22px] h-[22px] rounded-full flex items-center justify-center border-2 border-white shadow-sm"
                                      style={{ backgroundColor: cfg.bg }}
                                    >
                                      <Icon size={10} style={{ color: cfg.color }} />
                                    </div>
                                    {/* Content */}
                                    <div className="flex-1 bg-white rounded-lg border border-gray-100 px-3 py-2.5">
                                      <div className="flex items-center justify-between gap-2">
                                        <span className="text-xs font-bold text-gray-800">{event.title}</span>
                                        <span className="text-[10px] text-gray-400 flex-shrink-0">{fmt(event.date)} {fmtTime(event.date)}</span>
                                      </div>
                                      {event.detail && (
                                        <p className="text-[11px] text-gray-500 mt-1 line-clamp-2">{event.detail}</p>
                                      )}
                                      <div className="flex items-center gap-2 mt-1">
                                        {event.status && (() => {
                                          const st = STATUSES[event.status];
                                          return st ? (
                                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: st.bg, color: st.color }}>
                                              {st.label}
                                            </span>
                                          ) : null;
                                        })()}
                                        {event.priority && (() => {
                                          const PRIO = { hot: '#dc2626', warm: '#f97316', cold: '#6b7280', vip: '#7c3aed' };
                                          return (
                                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-gray-50" style={{ color: PRIO[event.priority] || '#6b7280' }}>
                                              {event.priority.toUpperCase()}
                                            </span>
                                          );
                                        })()}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <button
                      onClick={() => toggleExpand(cust.email)}
                      className="mt-4 w-full px-3 py-2 text-xs font-bold text-gray-500 border border-gray-200 rounded-lg hover:bg-white transition-all flex items-center justify-center gap-1"
                    >
                      <X size={12} /> Close
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CustomersTab;
